import {
  LAUNCH_VISIT_EVENTS,
  trackLocalOpsLaunchEvent,
} from './localOpsLaunchEvents';

const DISABLED_STORAGE_KEY = 'localops:funnelTelemetry:disabled';
const DEDUPE_STORAGE_KEY = 'localops:funnelTelemetry:dedupe:v1';
const EVENT_NAME_PATTERN = /^localops_[a-z0-9_]+$/;
const MAX_STRING_LENGTH = 160;

let testTransport = null;

export const LOCALOPS_FUNNEL_EVENTS = Object.freeze({
  CREATE_SHEET_OPENED: 'localops_create_sheet_opened',
  CREATE_TASK_TYPE_SELECTED: 'localops_create_task_type_selected',
  CREATE_DETAILS_STEP_REACHED: 'localops_create_details_step_reached',
  CREATE_LOCATION_CONFIRMED: 'localops_create_location_confirmed',
  CREATE_AMOUNT_SET: 'localops_create_amount_set',
  CREATE_REVIEW_STEP_REACHED: 'localops_create_review_step_reached',
  CREATE_PUBLISH_ATTEMPTED: 'localops_create_publish_attempted',
  CREATE_PUBLISH_BLOCKED: 'localops_create_publish_blocked',
  CREATE_TOPUP_REQUIRED: 'localops_create_topup_required',
  CREATE_TOPUP_STARTED: 'localops_create_topup_started',
  CREATE_PUBLISH_SUCCEEDED: 'localops_create_publish_succeeded',
  CREATE_PUBLISH_FAILED: 'localops_create_publish_failed',
  LOCATION_PRIMER_SHOWN: 'localops_location_primer_shown',
  LOCATION_BROWSE_FIRST_CLICKED: 'localops_location_browse_first_clicked',
  LOCATION_USE_MY_LOCATION_CLICKED: 'localops_location_use_my_location_clicked',
  LOCATION_PERMISSION_GRANTED: 'localops_location_permission_granted',
  LOCATION_PERMISSION_DENIED: 'localops_location_permission_denied',
  LOCATION_SAVE_ATTEMPTED: 'localops_location_save_attempted',
  LOCATION_SAVED: 'localops_location_saved',
  LOCATION_SAVE_FAILED: 'localops_location_save_failed',
  LOCATION_GATE_OPENED: 'localops_location_gate_opened',
  LOCATION_RETRY_CLICKED: 'localops_location_retry_clicked',
  LOCATION_RETRY_SUCCESS: 'localops_location_retry_success',
});

const SAFE_FIELD_TYPES = Object.freeze({
  regionSlug: 'string',
  marketSlug: 'string',
  source: 'string',
  src: 'string',
  utm_source: 'string',
  utm_campaign: 'string',
  intent: 'string',
  taskType: 'string',
  starterId: 'string',
  selectedType: 'string',
  entrySurface: 'string',
  surface: 'string',
  actionContext: 'string',
  gateReason: 'string',
  permissionState: 'string',
  errorCode: 'string',
  field: 'string',
  amountUSD: 'number',
  amountSource: 'string',
  paymentMode: 'string',
  requiresTopUp: 'boolean',
  shortfallUSD: 'number',
  hasPickup: 'boolean',
  hasDestination: 'boolean',
  hasDeliveryAddress: 'boolean',
  hasAmount: 'boolean',
  hasMedia: 'boolean',
  hasRequiredFields: 'boolean',
  hasExistingSavedLocation: 'boolean',
  wasRetry: 'boolean',
  isRideHelp: 'boolean',
  isGuidedTask: 'boolean',
  isOffering: 'boolean',
  isTask: 'boolean',
  reason: 'string',
  httpStatus: 'number',
  taskId: 'string',
  proofId: 'string',
  timestamp: 'timestamp',
});

const cleanString = (value, max = MAX_STRING_LENGTH) =>
  String(value || '')
    .replace(/[\r\n\t]+/g, ' ')
    .trim()
    .slice(0, max);

const getStorage = (storageName) => {
  if (typeof window === 'undefined') return null;
  try {
    return window[storageName] || null;
  } catch (err) {
    return null;
  }
};

const normalizeTimestamp = (value) => {
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? value.toISOString() : null;
  }
  if (typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : null;
  }
  if (typeof value === 'string') {
    const cleaned = cleanString(value, 80);
    const parsed = new Date(cleaned);
    return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : null;
  }
  return null;
};

const sanitizeNumber = (value) => {
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const sanitizeValue = (value, type) => {
  if (type === 'timestamp') return normalizeTimestamp(value);
  if (type === 'boolean') {
    return typeof value === 'boolean' ? value : undefined;
  }
  if (type === 'number') return sanitizeNumber(value);
  if (
    value == null ||
    typeof value === 'object' ||
    typeof value === 'function' ||
    typeof value === 'symbol'
  ) {
    return undefined;
  }
  const cleaned = cleanString(value);
  return cleaned || undefined;
};

export const sanitizeLocalOpsFunnelPayload = (payload = {}) => {
  const safe = {};
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    safe.timestamp = new Date().toISOString();
    return safe;
  }

  Object.entries(SAFE_FIELD_TYPES).forEach(([key, type]) => {
    if (!Object.prototype.hasOwnProperty.call(payload, key)) return;
    const sanitized = sanitizeValue(payload[key], type);
    if (sanitized !== undefined && sanitized !== null && sanitized !== '') {
      safe[key] = sanitized;
    }
  });

  if (!safe.timestamp) {
    safe.timestamp = new Date().toISOString();
  }

  return safe;
};

export const isLocalOpsFunnelTelemetryEnabled = () => {
  const envFlag = String(
    process.env.REACT_APP_LOCALOPS_FUNNEL_TELEMETRY_ENABLED || '',
  )
    .trim()
    .toLowerCase();
  if (envFlag === 'false') return false;

  const storage = getStorage('localStorage');
  if (!storage) return true;
  try {
    return storage.getItem(DISABLED_STORAGE_KEY) !== '1';
  } catch (err) {
    return true;
  }
};

const readDedupeState = () => {
  const storage = getStorage('sessionStorage');
  if (!storage) return null;
  try {
    const parsed = JSON.parse(storage.getItem(DEDUPE_STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch (err) {
    return {};
  }
};

const writeDedupeState = (state) => {
  const storage = getStorage('sessionStorage');
  if (!storage) return false;
  try {
    storage.setItem(DEDUPE_STORAGE_KEY, JSON.stringify(state));
    return true;
  } catch (err) {
    return false;
  }
};

const shouldSkipForDedupe = ({ dedupeKey, oncePerSession, ttlMs } = {}) => {
  const key = cleanString(dedupeKey, 220);
  if (!key || (!oncePerSession && !ttlMs)) return false;

  const state = readDedupeState();
  if (!state) return false;
  const now = Date.now();
  const lastSeen = Number(state[key]);
  const hasSeen = Number.isFinite(lastSeen) && lastSeen > 0;
  const ttl = Number(ttlMs);

  if (hasSeen && oncePerSession) return true;
  if (hasSeen && Number.isFinite(ttl) && ttl > 0 && now - lastSeen < ttl) {
    return true;
  }

  state[key] = now;
  writeDedupeState(state);
  return false;
};

const buildResult = ({ ok = false, status, eventName, payload }) => ({
  ok,
  status,
  eventName,
  payload,
});

const buildLaunchTransport = (eventName, payload, options) => {
  const slug = cleanString(options.regionSlug || payload.regionSlug, 80);
  if (!slug) return null;
  const isFunnelEvent =
    eventName.startsWith('localops_create_') ||
    eventName.startsWith('localops_location_');
  const launchEventName = isFunnelEvent
    ? LAUNCH_VISIT_EVENTS.CTA_CLICKED
    : eventName;
  return async () => {
    const sent = await trackLocalOpsLaunchEvent({
      slug,
      eventName: launchEventName,
      sourceTag: payload.src || payload.source || payload.utm_source,
      routeKind: payload.entrySurface || payload.surface,
      intent: payload.intent,
      ctaId: isFunnelEvent ? eventName : undefined,
      landingIntentType:
        payload.actionContext || payload.amountSource || undefined,
      targetKind:
        payload.reason ||
        payload.gateReason ||
        payload.errorCode ||
        payload.field ||
        payload.paymentMode ||
        undefined,
      starterId: payload.starterId,
    });
    if (!sent) {
      throw new Error('LocalOps launch telemetry was not sent');
    }
  };
};

const resolveTransport = (eventName, payload, options = {}) => {
  if (typeof options.transport === 'function') return options.transport;
  if (options.transport === 'launch') {
    return buildLaunchTransport(eventName, payload, options);
  }
  return testTransport;
};

export const recordLocalOpsFunnelEvent = async (
  eventName,
  payload = {},
  options = {},
) => {
  const safeEventName = cleanString(eventName, 96);
  const safePayload = sanitizeLocalOpsFunnelPayload(payload);

  if (!EVENT_NAME_PATTERN.test(safeEventName)) {
    return buildResult({
      status: 'invalid_event',
      eventName: safeEventName || undefined,
      payload: safePayload,
    });
  }

  if (!isLocalOpsFunnelTelemetryEnabled()) {
    return buildResult({
      status: 'disabled',
      eventName: safeEventName,
      payload: safePayload,
    });
  }

  if (shouldSkipForDedupe(options)) {
    return buildResult({
      status: 'deduped',
      eventName: safeEventName,
      payload: safePayload,
    });
  }

  const transport = resolveTransport(safeEventName, safePayload, options);
  if (!transport) {
    return buildResult({
      status: 'skipped',
      eventName: safeEventName,
      payload: safePayload,
    });
  }

  try {
    await Promise.resolve(
      transport({
        eventName: safeEventName,
        payload: safePayload,
        options,
      }),
    );
    return buildResult({
      ok: true,
      status: 'sent',
      eventName: safeEventName,
      payload: safePayload,
    });
  } catch (err) {
    return buildResult({
      status: 'failed',
      eventName: safeEventName,
      payload: safePayload,
    });
  }
};

export const __setLocalOpsFunnelTelemetryTransportForTests = (transport) => {
  testTransport = typeof transport === 'function' ? transport : null;
  return () => {
    testTransport = null;
  };
};
